import { useState, useEffect, Component } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, Menu, X, LogIn, UserCircle2 } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "@/lib/scroll";
import { loginWithGoogle } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
    { id: "servizi", label: "Servizi" },
    { id: "perche-noi", label: "Perché noi" },
    { id: "come-funziona", label: "Come funziona" },
    { id: "contatti", label: "Contatti" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then((res) => setUser(res.data))
            .catch(() => setUser(null));
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header className="fixed top-0 inset-x-0 z-50 glass-nav border-b border-line/70">
            <nav className="mx-auto max-w-7xl px-5 lg:px-10 h-[110px] lg:h-[120px] flex items-center justify-between">
                <Logo />

                <div className="hidden md:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="text-sm font-semibold text-mutedwarm hover:text-ink transition-colors duration-300"
                        >
                            {l.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    {user ? (
                        <button
                            data-testid="nav-account-link"
                            onClick={() => navigate("/area-personale")}
                            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-sand hover:-translate-y-0.5"
                        >
                            <UserCircle2 className="h-4 w-4 text-terracotta" />
                            Area personale
                        </button>
                    ) : (
                        <button
                            data-testid="nav-login-button"
                            onClick={loginWithGoogle}
                            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-sand hover:-translate-y-0.5"
                        >
                            <LogIn className="h-4 w-4 text-terracotta" />
                            Accedi
                        </button>
                    )}
                    <a
                        href="tel:+393522553391"
                        data-testid="nav-cta-call"
                        onClick={() => setOpen(false)}
                        className="hidden sm:inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5 hover:shadow-lg hover:shadow-terracotta/25"
                    >
                        <Phone className="h-4 w-4" />
                        Chiama ora
                    </a>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-ink"
                        aria-label={open ? "Chiudi menu" : "Apri menu"}
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden overflow-hidden border-t border-line/70 glass-nav"
                    >
                        <div className="px-5 py-6 flex flex-col gap-5">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="text-left font-serif text-2xl text-ink"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <a
                                href="tel:+393522553391"
                                data-testid="nav-mobile-cta-call"
                                className="inline-flex w-max items-center gap-2 rounded-full bg-terracotta px-6 py-3 font-bold text-cream"
                            >
                                <Phone className="h-4 w-4" />
                                352 255 3391
                            </a>
                            {!user && (
                                <button
                                    data-testid="nav-mobile-login"
                                    onClick={() => {
                                        setOpen(false);
                                        loginWithGoogle();
                                    }}
                                    className="inline-flex w-max items-center gap-2 rounded-full border border-ink/20 px-6 py-3 font-bold text-ink"
                                >
                                    <LogIn className="h-4 w-4 text-terracotta" />
                                    Accedi con Google
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
